<template>
  <div>
    <ts-modal
      ref="modal"
      modal-class="survey-question-modal"
      :modal-title="modalTitle"
    >
      <template #modal-content>
        <div class="row gx-2">
          <div class="mb-3 col-sm-6">
            <label
              for="question_type"
              class="form-label"
            >
              Type
            </label>
            <select
              id="question_type"
              v-model="questionData.typeId"
              :class="{'form-select': true, 'is-invalid': typeInvalid}"
            >
              <option
                v-for="qType in questionTypes"
                :key="qType.value"
                :value="qType.value"
              >
                {{ qType.name }}
              </option>
            </select>
            <div
              v-if="typeInvalid"
              class="invalid-feedback"
            >
              {{ typeErrorMessage }}
            </div>
          </div>
        </div>
        <div class="row gx-2">
          <div class="mb-3 col-sm-6">
            <label
              for="question_name"
              class="form-label"
            >
              Question
            </label>
            <input
              id="question_name"
              v-model="questionData.name"
              type="text"
              :class="{'form-control': true, 'is-invalid': nameInvalid}"
            >
            <div
              v-if="nameInvalid"
              class="invalid-feedback"
            >
              {{ nameErrorMessage }}
            </div>
          </div>
          <div class="mb-3 col-sm-6">
            <label
              for="question_name_fr"
              class="form-label"
            >
              Question (Français)
            </label>
            <input
              id="question_name_fr"
              v-model="questionData.nameFr"
              type="text"
              class="form-control"
            >
          </div>
        </div>
        <div class="row gx-2">
          <div class="mb-3 col">
            <label
              for="question_description"
              class="form-label"
            >
              Description
            </label>
            <textarea
              id="question_description"
              v-model="questionData.description"
              class="form-control"
            />
          </div>
          <div class="mb-3 col">
            <label
              for="question_description_fr"
              class="form-label"
            >
              Description (Français)
            </label>
            <textarea
              id="question_description_fr"
              v-model="questionData.descriptionFr"
              class="form-control"
            />
          </div>
        </div>
        <div
          v-show="questionData.typeId == 1"
          class="row gx-2"
        >
          <div class="mb-3 col-sm-6">
            <label
              for="question_group_name"
              class="form-label"
            >
              Question group
            </label>
            <select
              id="question_group_name"
              ref="groupName"
              v-model="questionData.groupName"
              class="form-select form-select-sm"
              data-allow-other="true"
              data-tags="simple"
            >
              <option />
              <option
                v-for="(groupName, groupIdx) of questionGroups"
                :key="`group-${groupIdx}`"
                :value="groupName"
              >
                {{ groupName }}
              </option>
            </select>
            <small class="form-text text-muted">Used in Evaluation form report, applies only to rating questions</small>
          </div>
        </div>
        <div class="row gx-2">
          <div class="mb-3 col-12">
            <label class="form-label">Options</label>
            <div>
              <div class="form-check">
                <input
                  id="question_is_mandatory"
                  v-model="questionData.isMandatory"
                  type="checkbox"
                  class="form-check-input"
                >
                <label
                  for="question_is_mandatory"
                  class="form-check-label"
                >
                  Mandatory question
                </label>
              </div>
              <div
                v-if="showSingleChoice || showMultipleChoice"
                class="form-check"
              >
                <input
                  id="question_choices_single_column"
                  v-model="questionData.options.choicesInSingleColumn"
                  type="checkbox"
                  class="form-check-input"
                >
                <label
                  for="question_choices_single_column"
                  class="form-check-label"
                >
                  Display choices in single column (by default splits in two columns if more than 4 choices)
                </label>
              </div>
              <div class="form-check">
                <input
                  id="question_allow_notes"
                  v-model="allowNotes"
                  type="checkbox"
                  class="form-check-input"
                  :disabled="showText"
                >
                <label
                  for="question_allow_notes"
                  class="form-check-label"
                >
                  Allow to provide additional comments (additional text field for comment input will be shown)
                </label>
              </div>
              <div
                v-if="allowNotes"
                class="ps-4"
              >
                <div class="form-check">
                  <input
                    id="question_notes_custom_label"
                    v-model="questionData.options.notesCustomLabel"
                    type="checkbox"
                    class="form-check-input"
                  >
                  <label
                    for="question_notes_custom_label"
                    class="form-check-label"
                  >
                    Change label for the comment box
                  </label>
                </div>
                <div
                  v-if="questionData.options.notesCustomLabel"
                  class="row gx-2 ps-4"
                >
                  <div class="mb-3 col-sm-6">
                    <label
                      for="question_notes_label"
                      class="form-label"
                    >
                      Comment label
                    </label>
                    <input
                      id="question_notes_label"
                      v-model="questionData.options.notesLabel"
                      type="text"
                      class="form-control"
                    >
                  </div>
                  <div class="mb-3 col-sm-6">
                    <label
                      for="question_notes_label_fr"
                      class="form-label"
                    >
                      Comment label (FR)
                    </label>
                    <input
                      id="question_notes_label_fr"
                      v-model="questionData.options.notesLabelFr"
                      type="text"
                      class="form-control"
                    >
                  </div>
                </div>
                <div class="row gx-2">
                  <div class="mb-3 col-sm-6">
                    <label
                      for="question_notes_min_length"
                      class="form-label"
                    >
                      Minimum comment length (characters)
                    </label>
                    <input
                      id="question_notes_min_length"
                      v-model="questionData.options.notesMinLength"
                      type="text"
                      class="form-control"
                    >
                  </div>
                </div>
              </div>
              <div
                v-if="showFileUpload"
                class="row gx-2"
              >
                <div class="mb-3 col-sm-6">
                  <label
                    for="question_notes_min_length"
                    class="form-label"
                  >
                    Maximum attachment count
                  </label>
                  <input
                    id="question_notes_min_length"
                    v-model="questionData.options.uploadCountMax"
                    type="text"
                    class="form-control"
                  >
                </div>
              </div>
              <div
                v-if="showSingleChoice"
                class="form-check"
              >
                <input
                  id="question_display_in_one_line"
                  v-model="questionData.options.displayInOneLine"
                  type="checkbox"
                  class="form-check-input"
                >
                <label
                  for="question_display_in_one_line"
                  class="form-check-label"
                >
                  Display choices in a row
                  <small class="form-text text-muted">Please note that "Add notes field will not function if this option is selected" </small>
                </label>
              </div>
              <div
                v-if="showText"
                class="row gx-2"
              >
                <div class="mb-3 col-sm-6">
                  <label
                    for="question_text_min_length"
                    class="form-label"
                  >
                    Minimum text length (characters)
                  </label>
                  <input
                    id="question_text_min_length"
                    v-model="questionData.options.textMinLength"
                    type="text"
                    class="form-control"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="showRating || showRatingsTable"
          class="row gx-2"
        >
          <div class="mb-3 col-auto rating-controls">
            <label class="form-label">Rating values (from - to)</label>
            <div>
              <input
                v-model="questionData.options.ratingMin"
                type="text"
                class="form-control"
              >
              &mdash;
              <input
                v-model="questionData.options.ratingMax"
                type="text"
                class="form-control"
              >

              <div class="form-check form-check-inline ms-4">
                <input
                  id="rating_not_applicable"
                  v-model="questionData.options.ratingNotApplicable"
                  type="checkbox"
                  class="form-check-input"
                >
                <label
                  for="rating_not_applicable"
                  class="form-check-label"
                >
                  Add not applicable option "NA"
                </label>
              </div>
            </div>
          </div>
          <div
            v-if="showRatingsTable"
            class="mb-3 col-auto"
          >
            <label
              for="ratings-table-choice-width"
              class="form-label"
            >
              Width of first column (in %)
            </label>
            <input
              id="ratings-table-choice-width"
              v-model="choiceColWidth"
              type="text"
              class="form-control text-center"
              style="width: 80px"
            >
          </div>
        </div>
        <ul
          v-if="showSingleChoice || showMultipleChoice || showRatingsTable"
          id="ratingsTab"
          class="nav nav-tabs"
          role="tablist"
        >
          <li
            class="nav-item"
            role="presentation"
          >
            <a
              id="choices-tab"
              ref="choicesTab"
              class="nav-link"
              data-bs-toggle="tab"
              href="#choices"
              role="tab"
              aria-controls="choices"
              aria-selected="true"
            >
              Choices
            </a>
          </li>
          <li
            v-if="showRatingsTable"
            class="nav-item"
            role="presentation"
          >
            <a
              id="definitions-tab"
              class="nav-link"
              data-bs-toggle="tab"
              href="#definitions"
              role="tab"
              aria-controls="definitions"
              aria-selected="false"
            >
              Rating definitions
            </a>
          </li>
        </ul>
        <div class="tab-content">
          <div
            id="choices"
            class="tab-pane fade"
            role="tabpanel"
            aria-labelledby="choices-tab"
          >
            <div class="card card--list mt-2">
              <div
                v-if="questionData.options.choices.length == 0"
                class="card-body text-muted"
              >
                No choices defined
              </div>
              <Sortable
                :list="questionData.options.choices"
                item-key="uid"
                tag="div"
                class="list-group list-group-flush border-top-0"
                :options="{ draggable: '.list-group-item', handle: '.sort-handle', animation: 150 }"
                @end="updateChoiceData"
              >
                <template #item="{element: choice}">
                  <div class="list-group-item">
                    <div class="row gx-2">
                      <div class="col-auto">
                        <span
                          class="glyphicons sortable sort-handle"
                          title="Drag to reorder items"
                        />
                      </div>
                      <div
                        v-if="showSingleChoice || showMultipleChoice"
                        class="col-auto"
                      >
                        <a
                          href="#"
                          @click.prevent="toggleChoiceSettings(choice)"
                        >
                          <span
                            class="glyphicons settings"
                            title="Settings for choice"
                          />
                        </a>
                      </div>
                      <div class="col">
                        <input
                          v-model="choice.name"
                          type="text"
                          class="form-control form-control-sm"
                          placeholder="Name"
                        >
                      </div>
                      <div class="col">
                        <input
                          v-model="choice.nameFr"
                          type="text"
                          class="form-control form-control-sm"
                          placeholder="Name (Français)"
                        >
                      </div>
                      <div class="col-auto">
                        <button
                          type="button"
                          class="btn btn-sm btn-danger"
                          @click.prevent="deleteChoice(choice)"
                        >
                          <span
                            class="glyphicons delete"
                            title="Delete choice"
                          />
                        </button>
                      </div>
                    </div>
                    <div
                      v-if="choiceSettingsVisible[choice.uid]"
                      class="choice-options"
                      style="padding-left: 3.4rem;"
                    >
                      <div class="row gx-2">
                        <div class="col">
                          <div class="form-check">
                            <input
                              :id="`show-notes-${choice.uid}`"
                              v-model="choice.notesField.show"
                              type="checkbox"
                              class="form-check-input"
                            >
                            <label
                              class="form-check-label"
                              :for="`show-notes-${choice.uid}`"
                            >
                              Add notes field
                            </label>
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="choice.notesField.show"
                        class="notes-field-details"
                        style="padding-left: 2rem;"
                      >
                        <div class="row gx-2">
                          <div class="col">
                            <div class="form-check">
                              <input
                                :id="`notes-mandatory-${choice.uid}`"
                                v-model="choice.notesField.isMandatory"
                                type="checkbox"
                                class="form-check-input"
                              >
                              <label
                                class="form-check-label"
                                :for="`notes-mandatory-${choice.uid}`"
                              >
                                Notes field is mandatory
                              </label>
                            </div>
                          </div>
                        </div>
                        <div class="row gx-2">
                          <div class="col mb-3">
                            <label
                              for="`notes-placeholder-${choice.uid}`"
                              class="form-label"
                            >
                              Placeholder text for notes field
                            </label>
                            <input
                              :id="`notes-placeholder-${choice.uid}`"
                              v-model="choice.notesField.placeholderText"
                              type="text"
                              class="form-control form-control-sm"
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </Sortable>
              <div class="card-footer">
                <button
                  type="button"
                  class="btn btn-sm btn-success"
                  @click.prevent="addChoice"
                >
                  Add choice
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="showRatingsTable"
            id="definitions"
            class="tab-pane fade"
            role="tabpanel"
            aria-labelledby="definitions-tab"
          >
            <div class="row gx-2">
              <div class="col my-2">
                <div class="form-check form-check-inline">
                  <input
                    id="show_rating_definitions"
                    v-model="questionData.options.showRatingDefinitions"
                    type="checkbox"
                    class="form-check-input"
                  >
                  <label
                    for="show_rating_definitions"
                    class="form-check-label"
                  >
                    Display rating definitions
                  </label>
                </div>
              </div>
            </div>
            <div>
              <div class="card card--list card--choice-definitions">
                <div
                  v-if="questionData.options.ratingDefinitions.length == 0"
                  class="card-body text-muted"
                >
                  Please specify rating value range
                </div>
                <div
                  v-else
                  class="list-group list-group-flush"
                >
                  <div
                    v-for="(definition, definitionIdx) in questionData.options.ratingDefinitions"
                    :key="definitionIdx"
                    class="list-group-item"
                  >
                    <div class="row gx-2">
                      <div class="col col-label">
                        {{ ratingDefinitionLabel(definitionIdx) }}
                      </div>
                      <div class="col">
                        <input
                          v-model="definition.name"
                          type="text"
                          class="form-control form-control-sm"
                          placeholder="Name"
                        >
                      </div>
                      <div class="col">
                        <input
                          v-model="definition.nameFr"
                          type="text"
                          class="form-control form-control-sm"
                          placeholder="Name (Français)"
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #modal-footer>
        <button
          v-if="canRestore"
          type="button"
          class="btn btn-sm btn-info restore"
          @click.prevent="restoreQuestion"
        >
          Restore
        </button>
        <button
          v-if="deleteButtonVisible"
          type="button"
          class="btn btn-sm btn-danger"
          @click.prevent="deleteQuestion"
        >
          Delete
        </button>
        <button
          type="button"
          class="btn btn-sm btn-secondary"
          data-bs-dismiss="modal"
        >
          Close
        </button>
        <button
          type="button"
          class="btn btn-sm btn-primary"
          @click.prevent="updateQuestion"
        >
          Update
        </button>
      </template>
    </ts-modal>
    <teleport
      to="#vue-portal-target"
      append
    >
      <modal-confirmation-rails
        ref="confirmationModal"
      >
        <template #confirmation="{ context }">
          <div
            v-if="hasDestroyWarning(context)"
            class="alert alert-danger"
          >
            <h4 class="alert-heading">Are you sure you want to delete question?</h4>
            <hr>
            <p>By deleting the question you will also delete all the answers that were provided by participants to this question!</p>
          </div>
        </template>
      </modal-confirmation-rails>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onUpdated, ref, useTemplateRef, watch, type PropType } from 'vue'
import $ from 'jquery'
import { Tab } from 'bootstrap'
import {Sortable} from "sortablejs-vue3";
import TsModal from './ts-modal.vue'
import ModalConfirmationRails, { type ConfirmationConfiguration } from './modal-confirmation-rails.vue'

import { type Question, type QuestionChoice, QuestionType, SurveySectionType } from './survey-types'
import validate from 'validate.js'

import { initializeAutocompleteField } from '../site-utils'
import {isNonEmptyString, sortSortableList} from '@/js/js-utils'
import type {SortableEvent} from "sortablejs";

interface QuestionTypeItem {
  value: QuestionType;
  name: string;
}

interface ValidationErrors {
  name?: string[];
  type?: string[];
}

defineProps({
  modalTitle: { default: '', type: String },
  questionGroups: { default: (): string[] => [], type: Array as PropType<string[]> }
})

const emit = defineEmits<{
  'success': [question: Question, parentQuestion: Question | null]
}>()

const defaultQuestion = (): Question => ({
  id: null,
  uid: null,
  typeId: null,
  rm: false,
  name: null,
  nameFr: null,
  description: null,
  descriptionFr: null,
  groupName: null,
  allowSubQuestions: false,
  allowNotes: false,
  isMandatory: false,
  options: {
    displayInOneLine: false,
    ratingMin: null,
    ratingMax: null,
    ratingNotApplicable: false,
    ratingDefinitions: [],
    ratingTableChoiceWidth: null,
    choices: [],
    sectionTypeId: SurveySectionType.general,
    showRatingDefinitions: false,
    notesMandatory: false,
    notesCustomLabel: false,
    notesLabel: '',
    notesLabelFr: '',
    notesMinLength: null,
    uploadCountMax: null,
    textMinLength: null,
    choicesInSingleColumn: false
  },
  subQuestions: []
})

const modal = useTemplateRef<InstanceType<typeof TsModal>>('modal')
const groupName = useTemplateRef<HTMLSelectElement>('groupName')
const choicesTab = useTemplateRef<HTMLAnchorElement>('choicesTab')
const confirmationModal = useTemplateRef<InstanceType<typeof ModalConfirmationRails>>('confirmationModal')

const canDelete = ref(false)
const parentQuestion = ref<Question | null>(null)
const questionData = ref<Question>(defaultQuestion())
const choiceSettingsVisible = ref<Record<string, boolean>>({})
const validationErrors = ref<ValidationErrors | null>(null)
const questionTypes = ref<QuestionTypeItem[]>([])

const nameInvalid = computed((): boolean => {
  return Boolean(validationErrors.value?.name && validationErrors.value.name.length > 0)
})

const nameErrorMessage = computed((): string | null => {
  if (nameInvalid.value) {
    return validationErrors.value?.name?.[0] || null
  }
  return null
})

const typeInvalid = computed((): boolean => {
  return Boolean(validationErrors.value?.type && validationErrors.value.type.length > 0)
})

const typeErrorMessage = computed((): string | null => {
  if (typeInvalid.value) {
    return validationErrors.value?.type?.[0] || null
  }
  return null
})

const deleteButtonVisible = computed((): boolean => {
  return canDelete.value && !questionData.value.rm
})

const canRestore = computed((): boolean => {
  return questionData.value.rm
})

const showRating = computed((): boolean => {
  return questionData.value.typeId === QuestionType.rating || questionData.value.typeId == QuestionType.ratingsTable
})

const showSingleChoice = computed((): boolean => {
  return questionData.value.typeId === QuestionType.singleChoice
})

const showMultipleChoice = computed((): boolean => {
  return questionData.value.typeId === QuestionType.multipleChoice
})

const allowSubQuestions = computed((): boolean => {
  return questionData.value.typeId === QuestionType.instructorAssessment
})

const showRatingsTable = computed((): boolean => {
  return questionData.value.typeId === QuestionType.ratingsTable
})

const showFileUpload = computed((): boolean => {
  return questionData.value.typeId == QuestionType.fileUpload
})

const showText = computed((): boolean => {
  return questionData.value.typeId == QuestionType.textField
})

// Text field questions have no comment box. The stored value is kept,
// so it is restored when the question type is changed back.
const allowNotes = computed({
  get(): boolean {
    return questionData.value.allowNotes && !showText.value
  },

  set(newValue: boolean): void {
    questionData.value.allowNotes = newValue
  }
})

const choiceColWidth = computed({
  get(): string {
    if (questionData.value.options.ratingTableChoiceWidth) {
      return questionData.value.options.ratingTableChoiceWidth.toString()
    }

    return ''
  },

  set(newValue: string): void {
    // set some value therefore it's always updated
    questionData.value.options.ratingTableChoiceWidth = -1

    const strVal = newValue.replace(/[^0-9]/g, '')
    if (strVal.length > 0) {
      const v = parseInt(strVal)
      if (!isNaN(v)) {
        if (v > 100) {
          questionData.value.options.ratingTableChoiceWidth = 100
        } else if (v < 0) {
          questionData.value.options.ratingTableChoiceWidth = 0
        } else {
          questionData.value.options.ratingTableChoiceWidth = v
        }
        return
      }
    }

    questionData.value.options.ratingTableChoiceWidth = null
  }
})

const ratingDefinitionLabel = (idx: number): string => {
  if (!showRatingsTable.value) {
    return '-'
  }

  const rMin = parseInt(questionData.value.options.ratingMin?.toString() || '')
  const rMax = parseInt(questionData.value.options.ratingMax?.toString() || '')

  if (isNaN(rMin) || isNaN(rMax)) {
    return '-'
  }

  const itemCount = rMax - rMin
  if (itemCount < 0) {
    return '-'
  }

  if (idx <= itemCount) {
    return (rMin + idx).toString()
  }

  if (idx == itemCount + 1 && questionData.value.options.ratingNotApplicable) {
    return 'NA'
  }

  return '-'
}

const onRatingRangeUpdate = (): void => {
  if (!showRatingsTable.value) {
    return
  }

  const rMin = parseInt(questionData.value.options.ratingMin?.toString() || '')
  let rMax = parseInt(questionData.value.options.ratingMax?.toString() || '')

  if (isNaN(rMin) || isNaN(rMax)) {
    return
  }

  if (questionData.value.options.ratingNotApplicable) {
    rMax += 1
  }

  const itemCount = rMax - rMin
  if (itemCount < 0) {
    return
  }

  if (questionData.value.options.ratingDefinitions.length > itemCount) {
    // reduce items from end
    while(questionData.value.options.ratingDefinitions.length > itemCount) {
      const last = questionData.value.options.ratingDefinitions[questionData.value.options.ratingDefinitions.length - 1]
      if (isNonEmptyString(last.name) || isNonEmptyString(last.nameFr)) {
        break
      }
      questionData.value.options.ratingDefinitions.splice(questionData.value.options.ratingDefinitions.length - 1, 1)
    }
  } else {
    // append items
    while (questionData.value.options.ratingDefinitions.length <= itemCount) {
      questionData.value.options.ratingDefinitions.push({
        name: '',
        nameFr: ''
      })
    }
  }
}

const selectFirstRatingsTab = (): void => {
  nextTick(() => {
    if (choicesTab.value) {
      Tab.getOrCreateInstance(choicesTab.value).show()
    }
  })
}

const showModal = (question: Question, selectedParentQuestion: Question | null, selectedCanDelete: boolean): void => {
  parentQuestion.value = selectedParentQuestion || null
  canDelete.value = selectedCanDelete

  questionTypes.value = [
    { value: QuestionType.rating, name: 'Rating (choose single value from range)' },
    { value: QuestionType.singleChoice, name: 'Single choice (select single value from list of values)' },
    { value: QuestionType.multipleChoice, name: 'Multiple choice (select multiple values from a list)' },
    { value: QuestionType.textField, name: 'Text field (provide answer in a text form)' },
    { value: QuestionType.fileUpload, name: 'File upload (attachments)' }
  ]

  if (!parentQuestion.value) {
    questionTypes.value.push({ value: QuestionType.ratingsTable, name: 'Ratings table (list of ratings questions)' })
    questionTypes.value.push({ value: QuestionType.instructorAssessment, name: 'Assessment of the trainers' })
  }

  validationErrors.value = null
  questionData.value = JSON.parse(JSON.stringify(question))

  // update groupName
  const groupNameEl = groupName.value
  if (groupNameEl instanceof HTMLSelectElement) {
    nextTick(() => {
      $(groupNameEl).trigger('change'); // Notify any JS components that the value changed
    })
  }

  if (Array.isArray(questionData.value.options.choices)) {
    for (const choice of questionData.value.options.choices) {
      choiceSettingsVisible.value[choice.uid] = choice.notesField.show
    }
  }

  modal.value?.show()
}

const hideModal = (): void => {
  modal.value?.hide()
}

const validateData = (): boolean => {
  const constraints = {
    name: {
      presence: { allowEmpty: false }
    },
    type: {
      presence: true,
      inclusion: [
        QuestionType.rating,
        QuestionType.singleChoice,
        QuestionType.multipleChoice,
        QuestionType.textField,
        QuestionType.instructorAssessment,
        QuestionType.ratingsTable,
        QuestionType.fileUpload
      ]
    }
  }

  const qData = {
    name: questionData.value.name,
    nameFr: questionData.value.nameFr,
    description: questionData.value.description,
    descriptionFr: questionData.value.descriptionFr,
    allowNotes: questionData.value.allowNotes,
    type: questionData.value.typeId
  }

  validationErrors.value = validate(qData, constraints)

  return !validationErrors.value
}

const deleteQuestion = (): void => {
  const confirmation: ConfirmationConfiguration = {
    title: 'Delete question confirmation',
    message: 'Are you sure?',
    type: 'danger',
    cancelActionTitle: 'Cancel',
    confirmActionTitle: 'Delete',
    confirmationCode: null,
    modalDialogClass: questionData.value.id !== null ? 'modal-lg' : null,
    context: {
      destroyWarning: questionData.value.id !== null
    },
  }

  confirmationModal.value?.show(null, confirmation, () => {
    questionData.value.rm = true
    confirmationModal.value?.hide()
    emit('success', questionData.value, parentQuestion.value)
  })
}

const restoreQuestion = (): void => {
  if (confirm('Are you sure?')) {
    questionData.value.rm = false
  }
}

const updateQuestion = (): void => {
  if (validateData()) {
    questionData.value.allowSubQuestions = allowSubQuestions.value
    questionData.value.allowNotes = allowNotes.value
    emit('success', questionData.value, parentQuestion.value)
  }
}

const addChoice = (): void => {
  const newUid = `choice-${ new Date().getTime() }`
  choiceSettingsVisible.value[newUid] = false
  questionData.value.options.choices.push({
    id: null,
    uid: newUid,
    name: '',
    nameFr: '',
    notesField: {
      show: false,
      isMandatory: false,
      placeholderText: ''
    }
  })
}

const deleteChoice = (choice: QuestionChoice): void => {
  if (confirm('Are you sure?')) {
    for (let i = 0; i < questionData.value.options.choices.length; i++) {
      if (questionData.value.options.choices[i].uid === choice.uid) {
        questionData.value.options.choices.splice(i, 1)
        break;
      }
    }
  }
}

const toggleChoiceSettings = (choice: QuestionChoice): void => {
  if (!choice.notesField.show) {
    choiceSettingsVisible.value[choice.uid] = !choiceSettingsVisible.value[choice.uid]
    choiceSettingsVisible.value = JSON.parse(JSON.stringify(choiceSettingsVisible.value))
  }
}

const updateChoiceData = (sortableEvent?: SortableEvent): void => {
  sortSortableList(questionData.value.options.choices, sortableEvent)
}

const hasDestroyWarning = (context: unknown): boolean => {
  return Boolean(context && typeof context === 'object' && 'destroyWarning' in context && context.destroyWarning)
}

watch(() => questionData.value.options.ratingMin, onRatingRangeUpdate)
watch(() => questionData.value.options.ratingMax, onRatingRangeUpdate)
watch(() => questionData.value.options.ratingNotApplicable, onRatingRangeUpdate)
watch(() => questionData.value.typeId, selectFirstRatingsTab)

onUpdated(() => {
  const modalEl = modal.value?.modalEl
  const groupNameEl = groupName.value
  if (groupNameEl && modalEl instanceof HTMLElement) {
    initializeAutocompleteField(groupNameEl, modalEl, () => {
      $(groupNameEl).on('select2:select', (e: any) => {
        questionData.value.groupName = e.params.data.id
      })
      $(groupNameEl).on('select2:unselect', () => {
        questionData.value.groupName = null
      })
    })
  }
})

defineExpose({
  hideModal,
  showModal,
})
</script>
